import { makeStyles } from "@mui/styles"
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import * as L from "leaflet"
import "leaflet/dist/leaflet.css"
import "leaflet-draw"
import "leaflet-draw/dist/leaflet.draw.css"
import "../../../resources/leaflet-draw/locale.ja"
import PropTypes from "prop-types"
import {
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Comment as CommentIcon,
  MyLocation as MyLocationIcon,
} from "@mui/icons-material"
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material"
import { Query } from "../../../manager/carto"
import PlaceSearch from "./controls/placeSearch"
import { RootDataContext } from "../index"
import GairojuManager from "../../../manager/gairoju"
import { Rows } from "../../../manager/carto"
import LabelSelector from "./controls/labelSelector"
import Zoom from "./controls/zoom"
import CartoQueryLayer from "../../../manager/cartoQueryLayer"
import GairojuMaster from "../../../manager/gairoju"
import MemoLayer from "./memoLayer"
import LayerSelector, { LayerType } from "./controls/layerSelector"
import "leaflet-rotate"
import "./map.css"
import _ from "@sera/ansuko"
import { eve } from "react-eve-hook"
import "leaflet.awesome-markers/dist/leaflet.awesome-markers.css"
import "leaflet.awesome-markers/dist/leaflet.awesome-markers.js"
import "@fortawesome/fontawesome-free/css/all.min.css"

const useStyles = makeStyles({
  root: {
    width: "100%",
    height: "100%",
  },
  memoOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1500,
    pointerEvents: "none",
  },
  openInNew: {
    position: "absolute !important",
    right: "8px !important",
    bottom: "30px !important",
    backgroundColor: "white !important",
    zIndex: "2000 !important",
  },
  indicator: {
    position: "absolute",
    width: "30px !important",
    height: "30px !important",
    bottom: "210px",
    zIndex: 1000,
    left: "12px",
    color: "#803517 !important",
  },
  searchPlace: {
    position: "absolute",
    zIndex: "1000",
    left: "16px",
    bottom: "24px",
    width: "600px",
    backgroundColor: "white",
    maxWidth: "calc(100% - 300px)",
  },
  labelSelector: {
    position: "absolute !important",
    zIndex: "1000",
    right: "16px",
    bottom: "24px",
    width: "300px",
  },
  zoom: {
    position: "absolute",
    zIndex: "1000",
    top: "16px",
    left: "16px",
    backgroundColor: "white",
  },
  currentLocation: {
    position: "absolute",
    top: "16rem",
    left: "1rem",
    zIndex: 400,
    width: "30px",
    height: "30px",
    backgroundColor: "white",
    border: "2px solid rgba(150, 150, 150, 0.4)",
    borderRadius: "8px",
    boxShadow: "1px 1px 3px #b8b8b8",
    "& .MuiButtonGroup-grouped": {
      backgroundColor: "white",
      borderColor: "transparent",
    },
  },
  memoButton: {
    position: "absolute",
    zIndex: 1600,
    top: "280px",
    right: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
  },
})

const styles = {
  highlightRouteBox: {
    background: "white",
    padding: "1rem",
    boxShadow: "1px 1px 8px #000",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
  },
  highlightRouteHeaderBox: {
    display: "flex",
    justifyContent: "end",
  },
  highlightRouteBoxRow: {
    display: "flex",
    flexDirection: "row",
  },
  highlightRouteLabel: {
    fontSize: "12px",
    fontWeight: "bold",
    width: "90px",
  },
  highlightRouteValue: {
    fontSize: "12px",
  },
}

const baseLayres = {
  "道路台帳(本土)": L.tileLayer(
    "https://tokyo-g-mgmt.s3.ap-northeast-1.amazonaws.com/tiles/dourodaicho/13000_tokyo-to/{z}/{x}/{y}.png",
    { tileSize: 256, maxNativeZoom: 21, maxZoom: 22 }
  ),
  "道路台帳(大島)": L.tileLayer(
    "https://tile-tokyo.s3.ap-northeast-1.amazonaws.com/13361_oshima-machi/{z}/{x}/{y}.png",
    { tileSize: 256, maxNativeZoom: 20, maxZoom: 22 }
  ),
  "道路台帳(三宅島)": L.tileLayer(
    "https://tile-tokyo.s3.ap-northeast-1.amazonaws.com/13381_miyakejima-mura/{z}/{x}/{y}.png",
    { tileSize: 256, maxNativeZoom: 21, maxZoom: 22 }
  ),
  地理院地形図: L.tileLayer(
    "https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png",
    { tileSize: 256, maxNativeZoom: 18, maxZoom: 22 }
  ),
  街区地図: L.tileLayer(
    "https://api.mapbox.com/styles/v1/pacificspatial/ckwv1ijut1z4i15ldi8xot35b/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoicGFjaWZpY3NwYXRpYWwiLCJhIjoiY2p1amlzYTVkMWh6NTN5bnF3cXZwa3F5YyJ9.3_50pwnF9CBMFhddvpE9-g",
    { tileSize: 256, maxNativeZoom: 18, maxZoom: 22 }
  ),
}

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
})

const cssTemplate = `
#layer {
  text-name: [%LABEL_COLUMN%];
  text-face-name: 'Unifont Medium';
  text-size: 14;
  text-fill: #b30000;
  text-label-position-tolerance: 0;
  text-halo-radius: 1;
  text-halo-fill: #ffffff;
  text-dy: -10;
  text-allow-overlap: false;
  text-avoid-edges: true;
  text-margin: 8;
  text-placement: point;
  text-placement-type: simple;
  text-placements: "N,S,E,W,NE,SE,NW,SW,14,12,10";
}
`

const MapView = (props) => {
  const classes = useStyles()
  const {
    state,
    setMapZoom,
    setMapSelectPolygon,
    setMapSelectRectangle,
    setRefreshTime,
  } = useContext(RootDataContext)
  const ref = useRef(null)
  const highlightRoutePropsRef = useRef()
  const currentLocationLayerRef = useRef(null)
  const watchIdRef = useRef(null)
  const [showIndicator, setShowIndicator] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const isDrawingRef = useRef(false)
  const [map, setMap] = useState(null)
  const [baseLayer, setBaseLayer] = useState(baseLayres["街区地図"])
  const [isMemoMode, setIsMemoMode] = useState(false)
  const [memoData, setMemoData] = useState(null)
  const [memoText, setMemoText] = useState("")
  const tempMarkerRef = useRef(null)
  const prevMapViewRef = useRef(null)
  const [labelCss, setLabelCss] = useState(
    cssTemplate.replace("%LABEL_COLUMN%", "tree_id")
  )
  const [layerType, setLayerType] = useState(null)
  const [openHighlightRouteDetail, setOpenHighlightRouteDetail] =
    useState(false)
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null)

  const mapBounds = L.latLngBounds(
    L.latLng(36.037315165621706, 140.0714097192842),
    L.latLng(26.330685944753263, 138.94792597236466)
  )

  const boundsOsima = L.latLngBounds(
    L.latLng(34.840904718903865, 139.50519793633455),
    L.latLng(34.64164281290127, 139.28190816621253)
  )

  const boundsMiyakejima = L.latLngBounds(
    L.latLng(34.17357701185628, 139.39308794653778),
    L.latLng(33.98876258714588, 139.6619043973745)
  )

  const boundsHondo = L.latLngBounds(
    L.latLng(35.96448081638249, 139.95913466593916),
    L.latLng(35.48732157033605, 138.86874170327764)
  )

  let loadCount = 0

  useEffect(() => {
    if (!map) {
      return
    }

    map.invalidateSize()
  }, [map, props.viewType, state.windowMode])

  useEffect(() => {
    if (!ref.current) {
      return
    }
    let m = L.map(ref.current, {
      maxZoom: 22,
      zoomControl: false,
      rotate: true,
      rotateControl: {
        closeOnZeroBearing: false,
      },
      ...props.mapOptions,
    })

    m.on("baselayerchange", (e) => {
      setBaseLayer(e.layer)
      if (e.name === "道路台帳(大島)") {
        m.fitBounds(boundsOsima, 13)
      } else if (e.name === "道路台帳(三宅島)") {
        m.fitBounds(boundsMiyakejima, 13)
      } else if (e.name === "道路台帳(本土)") {
        m.fitBounds(boundsHondo, 13)
      }
    }).on("zoomend", (e) => {
      setMapZoom(m.getZoom())
    })
    m.setMaxBounds(mapBounds)
    m.setView([35.6813706610132, 139.76727819517552], 13)
    L.control.layers(baseLayres).addTo(m)
    setMap(m)

    const drawControl = new L.Control.Draw({
      draw: {
        polygon: true,
        polyline: false,
        circle: false,
        rectangle: true,
        marker: false,
        circlemarker: false,
      },
    })
    m.addControl(drawControl)

    m.on(L.Draw.Event.DRAWSTART, onDrawStarted)
    m.on(L.Draw.Event.CREATED, onDrawCreated)
    m.on(L.Draw.Event.DELETED, onDrawDeleted)
    m.on(L.Draw.Event.DRAWSTOP, onDrawStopped)

    props.onMapInitialized && props.onMapInitialized(m)

    return () => {
      map?.remove()
      setMap(null)
      ref.current = null
      props.onMapInitialized && props.onMapInitialized(null)
    }
  }, [ref])

  const onDrawStarted = useCallback(() => {
    console.log("[Draw]", "start")
    isDrawingRef.current = true
  }, [])

  const onDrawCreated = useCallback((e) => {
    console.log("[Draw]", "created", e)
    switch (e.layerType) {
      case "polygon":
        setMapSelectPolygon(e.layer.getLatLngs())
        break
      case "rectangle":
        setMapSelectRectangle(e.layer.getLatLngs())
        break
      default:
        break
    }
  }, [])

  const onDrawDeleted = useCallback((e) => {
    console.log("[Draw]", "delete", e)
  }, [])

  const onDrawStopped = useCallback((e) => {
    console.log("[Draw]", "stop", e)
    isDrawingRef.current = false
  }, [])

  const updateLocationOnMap = useCallback(
    (position, disableFly = false) => {
      if (!map) return
      const { latitude, longitude, accuracy } = position.coords
      const latlng = L.latLng(latitude, longitude)
      if (!disableFly) {
        map.setView(latlng, 18)
      }

      if (currentLocationLayerRef.current) {
        map.removeLayer(currentLocationLayerRef.current)
      }
      const circle = L.circle(latlng, {
        radius: accuracy,
        color: "#1976d2",
        fillColor: "#1976d2",
        fillOpacity: 0.2,
        weight: 2,
      })

      const markerOptions = {
        radius: 6,
        fillColor: "#1976d2",
        color: "#ffffff",
        weight: 2,
        opacity: 1,
        fillOpacity: 0.9,
      }
      const marker = L.circleMarker(latlng, markerOptions)
      const layerGroup = L.layerGroup([circle, marker])
      layerGroup.addTo(map)
      currentLocationLayerRef.current = layerGroup
    },
    [map]
  )

  const onCurrentLocation = useCallback(() => {
    if (!map) return
    if (!window.navigator.geolocation) {
      return
    }
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    }

    const onError = (error) => {
      console.log("error", error)
    }

    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        updateLocationOnMap(position)
        watchIdRef.current = window.navigator.geolocation.watchPosition(
          (position2) => updateLocationOnMap(position2, true),
          onError,
          options,
        )
        // 移動してしまうと困るらしいので止めました。
        // if (watchIdRef.current != null) {
        //   window.navigator.geolocation.clearWatch(watchIdRef.current)
        // }
        // watchIdRef.current = window.navigator.geolocation.watchPosition(
        //   updateLocationOnMap,
        //   onError,
        //   options
        // )
      },
      onError,
      options
    )
  }, [map, updateLocationOnMap])

  const closeMemoPopup = useCallback((disabledMode = true) => {
    if (tempMarkerRef.current && map) {
      map.removeLayer(tempMarkerRef.current)
      tempMarkerRef.current = null
    }
    if (map && prevMapViewRef.current) {
      map.flyTo(prevMapViewRef.current.center, prevMapViewRef.current.zoom)
      prevMapViewRef.current = null
    }
    setMemoData(null)
    setMemoText("")
    if (disabledMode) {
      setIsMemoMode(false)
    }
  }, [map])

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return
      if (isMemoMode) {
        e.preventDefault()
        closeMemoPopup()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [isMemoMode, memoData, closeMemoPopup])

  const onSaveMemo = useCallback(async () => {
    if (!memoData) return
    const escaped = (memoText || "").replace(/'/g, "''")
    if (memoData.cartodb_id) {
      await Query(`
        UPDATE tokyo_roadsidetrees_memo
        SET memo = '${escaped}' 
        WHERE cartodb_id = ${memoData.cartodb_id}
      `)
    } else {
      await Query(`
        INSERT INTO tokyo_roadsidetrees_memo
        (the_geom, memo) VALUES
        (ST_SetSRID(ST_MakePoint(${memoData.longitude}, ${memoData.latitude}),4326), '${escaped}')
      `)
    }
    eve("EVENT_MEMO_UPDATED")
    closeMemoPopup(false)
  }, [memoData, memoText, closeMemoPopup])

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) {
        window.navigator.geolocation?.clearWatch(watchIdRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!map || !baseLayer) {
      return
    }
    baseLayer.addTo(map)

    return () => {
      map.removeLayer(baseLayer)
    }
  }, [map, baseLayer])

  const loadData = useCallback(() => {
    let sql = GairojuManager.selectLayerQuery(
      state.filterData,
      state.selectedData,
      state.mapSelectPolygon,
      state.mapSelectRectangle
    )
    if (!sql) {
      return
    }

    Rows(sql)
      .then((res) => {
        let latlngs = res.map((r) => [r.latitude, r.longitude])
        map.fitBounds(
          L.latLngBounds(latlngs, { padding: [50, 50], maxZoom: 19 })
        )
      })
      .catch((e) => {
        console.log("[Map]", "fit bounds query error", e)
      })
  }, [
    state.selectedData,
    state.filterData,
    state.mapSelectPolygon,
    state.mapSelectRectangle,
  ])

  useEffect(() => {
    if (!state.selectedData || !map) {
      return
    }
    loadData()
  }, [state.selectedData])

  const openMemoForEdit = useCallback((row) => {
    setMemoData({
      cartodb_id: row.cartodb_id,
      latitude: row.latitude,
      longitude: row.longitude,
      memo: row.memo,
    })
    setMemoText(row.memo || "")
  }, [])

  const onDeleteMemoClick = useCallback((row) => {
    setDeleteConfirmRow(row)
  }, [])

  const onDeleteMemo = useCallback(async () => {
    if (!deleteConfirmRow?.cartodb_id) return
    await Query(`
      DELETE FROM tokyo_roadsidetrees_memo
      WHERE cartodb_id = ${deleteConfirmRow.cartodb_id}
    `)
    eve("EVENT_MEMO_UPDATED")
    setDeleteConfirmRow(null)
  }, [deleteConfirmRow])

  const onOpenMemo = useCallback(
    (lat, lng) => {
      if (!map) return
      if (tempMarkerRef.current) {
        map.removeLayer(tempMarkerRef.current)
      }
      prevMapViewRef.current = {
        center: map.getCenter(),
        zoom: map.getZoom(),
      }
      const data = { latitude: lat, longitude: lng, memo: "" }
      setMemoData(data)
      setMemoText(data.memo || "")

      const memoIcon = L.AwesomeMarkers?.icon
        ? L.AwesomeMarkers.icon({
            prefix: "fa",
            icon: "comment",
            markerColor: "blue",
          })
        : undefined
      const marker = L.marker([lat, lng], memoIcon ? { icon: memoIcon } : {})
      marker.addTo(map)
      tempMarkerRef.current = marker

      map.flyTo([lat, lng], 18)
    },
    [map]
  )

  useEffect(() => {
    if (!isMemoMode) {
      closeMemoPopup()
    }
  }, [isMemoMode, closeMemoPopup])

  useEffect(() => {
    if (!map) return
    const el = map.getContainer()
    if (isMemoMode) {
      el.classList.add("leaflet-crosshair")
    }
    return () => {
      el.classList.remove("leaflet-crosshair")
    }
  }, [map, isMemoMode])

  useEffect(() => {
    if (!isMemoMode || !map) return
    const handler = (e) => {
      onOpenMemo(e.latlng.lat, e.latlng.lng)
    }
    map.on("click", handler)
    return () => map.off("click", handler)
  }, [isMemoMode, map, onOpenMemo])

  useEffect(() => {
    if (!map || !memoData?.latitude) {
      return
    }
    if (!prevMapViewRef.current) {
      prevMapViewRef.current = {
        center: map.getCenter(),
        zoom: map.getZoom(),
      }
    }
    map.flyTo([memoData.latitude, memoData.longitude], 18)
  }, [map, memoData])

  useEffect(() => {
    if (memoData?.memo !== undefined) {
      setMemoText(memoData.memo || "")
    }
  }, [memoData])

  const openNewWindow = () => {
    window.open(
      "/map",
      null,
      "width=300,toolbar=yes,menubar=yes,scrollbars=yes"
    )
  }

  const onTileLoadStart = () => {
    loadCount++
    indicatorStart()
  }

  const onTileLoaded = () => {
    loadCount--
    if (loadCount < 0) loadCount = 0
    if (loadCount === 0) {
      indicatorStop()
    }
  }

  useEffect(() => {
    console.log("[Map]", "is drawing update", isDrawing)
  }, [isDrawing])

  const onPointClicked = useCallback(
    (treeId) => {
      openDetailDialog(treeId)
    },
    [isDrawing, props]
  )

  const openDetailDialog = useCallback(
    (treeId) => {
      if (isDrawingRef.current) {
        return
      }
      props.showDetailDialogByTreeId(treeId)
    },
    [props]
  )

  const indicatorStart = () => {
    setShowIndicator(true)
  }

  const indicatorStop = () => {
    setShowIndicator(false)
  }

  const onPlaceSelected = (place) => {
    map.setView(place.center, 17, {
      animate: true,
      pan: {
        duration: 1.5,
      },
    })
  }

  const onPointClickedFromTable = _.debounce((e) => {
    if (highlightRoutePropsRef.current) {
      return
    }
    openDetailDialog(e.data["tree_id"])
    return true
  }, 100)

  const onHighlightRouteClick = useCallback(
    (e) => {
      highlightRoutePropsRef.current = e.data
      setOpenHighlightRouteDetail(true)
    },
    [isDrawing]
  )

  return (
    <>
      {props.enableNewWindowButton && (
        <IconButton className={classes.openInNew} onClick={openNewWindow}>
          <OpenInNewIcon />
        </IconButton>
      )}
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        <div className={classes.root} ref={ref} />
        {isMemoMode && map && (
          <div className={classes.memoOverlay} aria-label="メモ" />
        )}
      </Box>
      <CartoQueryLayer
        map={map}
        key="tree_layer"
        cartoCss={`
      #layer {
    marker-width: 10;
    marker-fill: [fill_color];
    marker-fill-opacity: 0.75;
    marker-line-color: #FFFFFF;
    marker-line-width: 1;
    marker-line-opacity: 1;
    marker-placement: point;
    marker-type: ellipse;
    marker-allow-overlap: true;
  }        
        `}
        query={GairojuMaster.tableLayerQuery(
          state.filterData,
          state.mapSelectPolygon,
          state.mapSelectRectangle
        )}
        featureClickColumns={["tree_id"]}
        onFeatureClick={onPointClickedFromTable}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        zIndex={1000}
      />
      <CartoQueryLayer
        map={map}
        key="tree_layer_selected"
        cartoCss={`#layer {
          marker-width: 12;
          marker-fill: [selected_fill_color];
          marker-fill-opacity: 0.75;
          marker-line-color: #FFFFFF;
          marker-line-width: 1;
          marker-line-opacity: 1;
          marker-placement: point;
          marker-type: ellipse;
          marker-allow-overlap: true;
        }`}
        query={GairojuMaster.selectLayerQuery(
          state.filterData,
          state.selectedData,
          state.mapSelectPolygon,
          state.mapSelectRectangle
        )}
        featureClickColumns={["tree_id"]}
        onFeatureClick={onPointClicked}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        zIndex={2000}
        hidden={!state.selectedData}
      />
      <CartoQueryLayer
        map={map}
        key="label_layer"
        cartoCss={cssTemplate.replace("%LABEL_COLUMN%", state.mapLabel)}
        query={GairojuMaster.labelLayerQuery(
          state.filterData,
          state.mapSelectPolygon,
          state.mapSelectRectangle
        )}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        hidden={!state.mapLabel || state.mapZoom < 19}
        zIndex={1900}
      />
      <CartoQueryLayer
        map={map}
        key="koku_layer"
        cartoCss={`
        #layer {
          polygon-fill: #6d8eba;
          polygon-opacity: 0.3;
        }
        #layer::outline {
          line-width: 5;
          line-color: #6f7e72;
          line-opacity: 0.5;
        }
        #layer::labels {
          text-name: [koku_name];
          text-face-name: 'Unifont Medium';
          text-size: 18;
          text-fill: #333333;
          text-label-position-tolerance: 0;
          text-halo-radius: 1;
          text-halo-fill: #6F808D;
          text-dy: -10;
          text-allow-overlap: true;
          text-placement: point;
          text-placement-type: dummy;
        }
        `}
        query={GairojuMaster.kokuLayerQuery()}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        hidden={layerType !== LayerType.Koku}
        zIndex={800}
      />
      <CartoQueryLayer
        map={map}
        key="gyoseikai_layer"
        cartoCss={`
        #layer {
          polygon-fill: #89ba6d;
          polygon-opacity: 0.3;
        }
        #layer::outline {
          line-width: 5;
          line-color: #6f7e72;
          line-opacity: 0.5;
        }
        #layer::labels {
          text-name: [cityname];
          text-face-name: 'Unifont Medium';
          text-size: 18;
          text-fill: #333333;
          text-label-position-tolerance: 0;
          text-halo-radius: 1;
          text-halo-fill: #6F808D;
          text-dy: -10;
          text-allow-overlap: true;
          text-placement: point;
          text-placement-type: dummy;
        }
        `}
        query={GairojuMaster.gyoseikaiLayerQuery()}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        hidden={layerType !== LayerType.Gyoseikai}
        zIndex={800}
      />
      <CartoQueryLayer
        map={map}
        key="road_grid_layer"
        cartoCss={`
        #layer {
          polygon-fill: #ffffff;
          polygon-opacity: 0.01;
        }
        #layer::outline {
          line-width: 1;
          line-color: #ff0000;
          line-opacity: 0.5;
        }
        #layer::labels {
          text-name: [roadgrid];
          text-face-name: 'Lato Bold';
          text-size: 12;
          text-fill: #f81d1d;
          text-label-position-tolerance: 0;
          text-halo-radius: 1;
          text-halo-fill: #ffffff;
          text-dy: -15;
          text-allow-overlap: false;
          text-placement: interior;
          text-placement-type: dummy;
        }
        `}
        query={GairojuMaster.roadGridLayerQuery()}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        hidden={layerType !== LayerType.RoadGrid}
        zIndex={800}
      />
      <CartoQueryLayer
        map={map}
        key="highlight_route_layer"
        cartoCss={`
        #layer {
          line-cap: round;
          line-join: round;
        }
        #layer[zoom<9] {
          ::case {
              line-width: 2;
              line-opacity: 0.6;
              line-color: #b1b1b1;
          }
          ::fill {
              line-width: 1;
              line-color: [colorcode];  
          }
        }
        #layer[zoom>=9][zoom<12] {
          ::case {
              line-width: 4.5;
              line-opacity: 0.6;
              line-color: #b1b1b1;
          }
          ::fill {
              line-width: 3;
              line-color: [colorcode];  
          }
        }
        #layer[zoom>=12][zoom<15] {
          ::case {
              line-width: 6.5;
              line-opacity: 0.6;
              line-color: #b1b1b1;
          }
          ::fill {
              line-width: 5;
              line-color: [colorcode];  
          }
        }
        #layer[zoom>=15] {
          ::case {
              line-width: 10;
              line-opacity: 0.6;
              line-color: #b1b1b1;
          }
          ::fill {
              line-width: 8;
              line-color: [colorcode];  
          }
        }
        `}
        query={GairojuMaster.highlightRouteLayerQuery(
          state.filterData,
          state.mapSelectPolygon,
          state.mapSelectRectangle
        )}
        featureClickColumns={[
          "route",
          "route_nickname",
          "name",
          "flower_month_ja",
          "autumnleaves_month_ja",
        ]}
        onFeatureClick={onHighlightRouteClick}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        hidden={layerType !== LayerType.HighlightRoute}
        zIndex={1000}
      />
      <CartoQueryLayer
        map={map}
        key="route_layer"
        cartoCss={`
        #layer {
          polygon-fill: #666666;
          polygon-opacity: 0.4;
        }
        #layer::outline {
          line-width: 1;
          line-color: #3b3b3b;
          line-opacity: 0.5;
        }
        #layer::labels {
          text-name: [route_name];
          text-face-name: 'Unifont Medium';
          text-fill: #003388;
          text-halo-radius: 0.3;
          text-halo-fill: #ffffff;
          text-dy: -10;
          text-allow-overlap: false;
          text-placement: line;
          
          [zoom>=12] { text-opacity: 0; }
          [zoom>=14] { text-opacity: 1; text-size: 10; }
          [zoom>=16] { text-opacity: 1; text-size: 12; }
          [zoom>=18] { text-opacity: 1; text-size: 18; }
        }
        `}
        query={GairojuMaster.routeLayerQuery()}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        hidden={layerType !== LayerType.Route}
        zIndex={800}
      />
      <CartoQueryLayer
        map={map}
        key="yudoin-layer"
        cartoCss={`
        #layer {
          polygon-fill: #ff3333;
          polygon-opacity: 0.6;
        }
        #layer::outline {
          line-width: 1;
          line-color: #3b3b3b;
          line-opacity: 0.5;
        }
        `}
        query={GairojuMaster.yudoinLayerQuery()}
        onLoading={onTileLoadStart}
        onLoadFinished={onTileLoaded}
        hidden={layerType !== LayerType.Yudoin}
        zIndex={800}
      />
      <MemoLayer
        map={map}
        isMemoMode={isMemoMode}
        onMemoClick={openMemoForEdit}
        onDeleteMemoClick={onDeleteMemoClick}
      />
      <PlaceSearch
        className={classes.searchPlace}
        onPlaceSelected={onPlaceSelected}
      />
      <ButtonGroup
        orientation="vertical"
        variant="contained"
        aria-label="現在地"
        className={classes.currentLocation}
      >
        <IconButton
          size="small"
          onClick={onCurrentLocation}
          aria-label="現在地"
        >
          <MyLocationIcon fontSize="small" />
        </IconButton>
      </ButtonGroup>
      <Box className={classes.memoButton}>
        <IconButton
          onClick={() => setIsMemoMode((prev) => !prev)}
          aria-label="メモ"
          color={isMemoMode ? "primary" : "default"}
          sx={{
            width: 36,
            height: 36,
            backgroundColor: isMemoMode ? "primary.light" : "white",
            border: "2px solid rgba(150, 150, 150, 0.4)",
            borderRadius: "8px",
            boxShadow: "1px 1px 3px #b8b8b8",
          }}
        >
          <CommentIcon fontSize="small" />
        </IconButton>
      </Box>
      <LabelSelector className={classes.labelSelector} />
      <LayerSelector
        onChange={(e) => {
          console.log(e)
          setLayerType(e)
        }}
        sx={{
          position: "absolute",
          zIndex: "900",
          backgroundColor: "#fff",
          width: "120px",
          top: "72px",
          borderColor: "#969292",
          borderStyle: "solid",
          borderWidth: "1px",
          borderRadius: "8px",
          padding: "8px",
          fontSize: "12px",
          boxShadow: "1px 1px 3px #b8b8b8",
          right: "8px",
        }}
      />
      {showIndicator && (
        <CircularProgress className={classes.indicator} color="secondary" />
      )}
      <Zoom className={classes.zoom} map={map} />
      <Backdrop open={openHighlightRouteDetail} style={{ zIndex: 2000 }}>
        {highlightRoutePropsRef.current && (
          <Box style={styles.highlightRouteBox}>
            <Box style={styles.highlightRouteHeaderBox}>
              <IconButton size="small">
                <CloseIcon
                  size="small"
                  onClick={() => {
                    highlightRoutePropsRef.current = null
                    setOpenHighlightRouteDetail(false)
                  }}
                />
              </IconButton>
            </Box>
            <Box style={styles.highlightRouteBoxRow}>
              <Typography style={styles.highlightRouteLabel}>
                整理番号
              </Typography>
              <Typography style={styles.highlightRouteValue}>
                {highlightRoutePropsRef.current.route}
              </Typography>
            </Box>
            <Box style={styles.highlightRouteBoxRow}>
              <Typography style={styles.highlightRouteLabel}>
                通称道路名
              </Typography>
              <Typography style={styles.highlightRouteValue}>
                {highlightRoutePropsRef.current.route_nickname}
              </Typography>
            </Box>
            <Box style={styles.highlightRouteBoxRow}>
              <Typography style={styles.highlightRouteLabel}>種名</Typography>
              <Typography style={styles.highlightRouteValue}>
                {highlightRoutePropsRef.current.name}
              </Typography>
            </Box>
            <Box style={styles.highlightRouteBoxRow}>
              <Typography style={styles.highlightRouteLabel}>
                開花時期
              </Typography>
              <Typography style={styles.highlightRouteValue}>
                {highlightRoutePropsRef.current.flower_month_ja}
              </Typography>
            </Box>
            <Box style={styles.highlightRouteBoxRow}>
              <Typography style={styles.highlightRouteLabel}>
                紅葉時期
              </Typography>
              <Typography style={styles.highlightRouteValue}>
                {highlightRoutePropsRef.current.autumnleaves_month_ja}
              </Typography>
            </Box>
          </Box>
        )}
      </Backdrop>
      <Dialog
        open={!!deleteConfirmRow}
        onClose={() => setDeleteConfirmRow(null)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 2000 }}
      >
        <DialogTitle>メモを削除</DialogTitle>
        <DialogContent>
          <Alert severity="warning">本当に削除しますか？</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmRow(null)}>キャンセル</Button>
          <Button color="error" variant="contained" onClick={onDeleteMemo}>
            削除
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!memoData}
        onClose={closeMemoPopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minWidth: 320 } }}
        sx={{ zIndex: 2000 }}
      >
        <DialogTitle>メモ</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            value={memoText}
            onChange={(e) => setMemoText(e.target.value)}
            placeholder="メモを入力..."
            variant="outlined"
            margin="dense"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => setMemoText(memoData?.memo ?? "")}
          >
            リセット
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeMemoPopup}>キャンセル</Button>
          <Button variant="contained" onClick={onSaveMemo}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

MapView.propTypes = {
  enableNewWindowButton: PropTypes.bool,
  showDetailDialogByTreeId: PropTypes.func,
  history: PropTypes.any,
  match: PropTypes.any,
  location: PropTypes.any,
  mapOptions: PropTypes.any,
  viewType: PropTypes.any,
  onMapInitialized: PropTypes.func,
}

export default MapView
